import { useState, useCallback, useMemo } from "react";

/**
 * Manage checkbox selection state for bulk operations
 *
 * Features:
 * - Toggle single item
 * - Toggle all items
 * - Range selection (Shift+Click)
 * - Clear selection
 * - Check if item/all selected
 */
export function useIssueSelection(allIssueIds: string[] = []) {
	const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

	/**
	 * Toggle single item selection
	 */
	const toggle = useCallback((id: string) => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (next.has(id)) {
				next.delete(id);
			} else {
				next.add(id);
			}
			return next;
		});
	}, []);

	/**
	 * Toggle all items (select all or clear all)
	 */
	const toggleAll = useCallback(() => {
		setSelectedIds((prev) => {
			if (prev.size === allIssueIds.length) {
				return new Set();
			}
			return new Set(allIssueIds);
		});
	}, [allIssueIds]);

	/**
	 * Select range of items (for Shift+Click)
	 */
	const selectRange = useCallback(
		(fromId: string, toId: string) => {
			const fromIndex = allIssueIds.indexOf(fromId);
			const toIndex = allIssueIds.indexOf(toId);
			if (fromIndex === -1 || toIndex === -1) return;

			const start = Math.min(fromIndex, toIndex);
			const end = Math.max(fromIndex, toIndex);
			const rangeIds = allIssueIds.slice(start, end + 1);

			setSelectedIds((prev) => {
				const next = new Set(prev);
				for (const id of rangeIds) {
					next.add(id);
				}
				return next;
			});
		},
		[allIssueIds],
	);

	/**
	 * Clear all selections
	 */
	const clear = useCallback(() => {
		setSelectedIds(new Set());
	}, []);

	/**
	 * Check if a specific item is selected
	 */
	const isSelected = useCallback(
		(id: string) => selectedIds.has(id),
		[selectedIds],
	);

	/**
	 * Check if all items are selected
	 */
	const isAllSelected = useMemo(
		() => allIssueIds.length > 0 && selectedIds.size === allIssueIds.length,
		[allIssueIds.length, selectedIds.size],
	);

	return {
		selectedIds: Array.from(selectedIds),
		selectedCount: selectedIds.size,
		toggle,
		toggleAll,
		selectRange,
		clear,
		isSelected,
		isAllSelected,
	};
}
